import { useInfiniteListFavoritedMe, useUnreads } from "@/tanstack-hooks";
import EmptyState from "./EmptyState";
import { CloudOffIcon, Heart } from "lucide-react";
import Spinner from "./Spinner";
import ProfileCard from "./profile/ProfileCard";
import { useEffect, useRef, useState } from "react";
import { FavoriteSortBy } from "@/generated-api";
import { useInView } from "react-intersection-observer";
import ScrollTopButton from "./ScrollTopButton";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import PulseLoader from "./PulseLoader";

export default function FavoritedMeList({
  sortBy,
}: {
  sortBy: FavoriteSortBy;
}) {
  const { ref, inView } = useInView({
    rootMargin: "400px 0px", // Starts observing 200px before the element comes into view
    threshold: 0,
  });
  const location = useLocation();
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteListFavoritedMe(sortBy);
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const pageRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const unreadsQuery = useUnreads();
  const t = useTranslation();

  useEffect(() => {
    void unreadsQuery.refetch();
    const timer = setTimeout(() => {
      setIsInitialDelay(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleCardClick = () => {
    if (pageRef.current) {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
  };

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(location.pathname);
    if (savedScroll !== null) {
      const timer = setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll));
        sessionStorage.removeItem(location.pathname);
        setIsLoaded(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // If no saved scroll, we can show content immediately
      setIsLoaded(true);
    }
  }, [data]);

  // Debounced fetch next page
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isInitialDelay) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a new timeout for fetching the next page
      fetchTimeoutRef.current = setTimeout(() => {
        void fetchNextPage();
      }, 250);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isInitialDelay]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] w-full mx-auto">
        <PulseLoader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.FAVORITES_PAGE.ERRORS.LOADING}
        message={t.FAVORITES_PAGE.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  const favoritedMe = data.pages.flatMap((page) => page.items);

  return (
    <>
      {favoritedMe.length === 0 ? (
        <EmptyState
          icon={Heart}
          title={t.FAVORITES_PAGE.LABELS.FAVORITED_ME_EMPTY}
          message={t.FAVORITES_PAGE.LABELS.FAVORITED_ME_EMPTY_DESCRIPTION}
        />
      ) : (
        <div
          className={`flex flex-col w-full mt-2 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          ref={pageRef}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {favoritedMe.map((profile) => (
              <ProfileCard
                profile={profile}
                key={profile.user_id}
                onClick={handleCardClick}
              />
            ))}
          </div>

          <div
            ref={ref}
            className="w-full h-20 flex items-center justify-center"
          >
            {isFetchingNextPage && <Spinner />}
          </div>
          <ScrollTopButton />
        </div>
      )}
    </>
  );
}
